import { useState, useEffect } from "react";
import { settingsAPI } from "../../services/api";
import toast from "react-hot-toast";
import {
  Settings,
  Save,
  RefreshCw,
  MapPin,
  DollarSign,
  Truck,
  Globe,
} from "lucide-react";

const SettingsManagement = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    deliveryLimit: "",
    deliveryCharge: "",
    freeDeliveryThreshold: "",
    businessName: "",
    businessPhone: "",
    businessAddress: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      const settingsData = response.data.data.settings;

      setSettings(settingsData);
      setFormData({
        deliveryLimit: settingsData.deliveryLimit || "10",
        deliveryCharge: settingsData.deliveryCharge || "50",
        freeDeliveryThreshold: settingsData.freeDeliveryThreshold || "500",
        businessName: settingsData.businessName || "Grainly",
        businessPhone: settingsData.businessPhone || "",
        businessAddress: settingsData.businessAddress || "",
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSaveSetting = async (key, value, description, category) => {
    try {
      setSaving(true);
      await settingsAPI.update(key, {
        value,
        description,
        category,
      });

      setSettings((prev) => ({
        ...prev,
        [key]: value,
      }));

      toast.success(`${key} updated successfully`);
    } catch (error) {
      console.error(`Error updating ${key}:`, error);
      toast.error(`Failed to update ${key}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);

      const settingsToUpdate = [
        {
          key: "deliveryLimit",
          value: parseFloat(formData.deliveryLimit),
          description: "Maximum delivery distance in kilometers",
          category: "delivery",
        },
        {
          key: "deliveryCharge",
          value: parseFloat(formData.deliveryCharge),
          description: "Standard delivery charge in INR",
          category: "delivery",
        },
        {
          key: "freeDeliveryThreshold",
          value: parseFloat(formData.freeDeliveryThreshold),
          description: "Minimum order amount for free delivery in INR",
          category: "delivery",
        },
        {
          key: "businessName",
          value: formData.businessName,
          description: "Business name displayed to customers",
          category: "business",
        },
        {
          key: "businessPhone",
          value: formData.businessPhone,
          description: "Business contact phone number",
          category: "business",
        },
        {
          key: "businessAddress",
          value: formData.businessAddress,
          description: "Business address",
          category: "business",
        },
      ];

      // Update all settings
      await Promise.all(
        settingsToUpdate.map((setting) =>
          settingsAPI.update(setting.key, {
            value: setting.value,
            description: setting.description,
            category: setting.category,
          })
        )
      );

      // Update local state
      const newSettings = {};
      settingsToUpdate.forEach((setting) => {
        newSettings[setting.key] = setting.value;
      });
      setSettings((prev) => ({ ...prev, ...newSettings }));

      toast.success("All settings updated successfully");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#305669]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Settings className="h-8 w-8 text-[#305669]" />
            <h1 className="text-3xl font-bold text-gray-900">
              Settings Management
            </h1>
          </div>
          <p className="text-gray-600">
            Configure your business settings and delivery parameters
          </p>
        </div>

        <div className="space-y-6">
          {/* Delivery Settings */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Truck className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Delivery Settings
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Delivery Limit (km)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.deliveryLimit}
                  onChange={(e) =>
                    handleInputChange("deliveryLimit", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum distance for delivery in kilometers
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="inline h-4 w-4 mr-1" />
                  Delivery Charge (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.deliveryCharge}
                  onChange={(e) =>
                    handleInputChange("deliveryCharge", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="50"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Standard delivery charge in INR
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="inline h-4 w-4 mr-1" />
                  Free Delivery Threshold (₹)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.freeDeliveryThreshold}
                  onChange={(e) =>
                    handleInputChange("freeDeliveryThreshold", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum order amount for free delivery
                </p>
              </div>
            </div>
          </div>

          {/* Business Settings */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Globe className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Business Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name
                </label>
                <input
                  type="text"
                  value={formData.businessName}
                  onChange={(e) =>
                    handleInputChange("businessName", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="Grainly"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Phone
                </label>
                <input
                  type="tel"
                  value={formData.businessPhone}
                  onChange={(e) =>
                    handleInputChange("businessPhone", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="+91 9876543210"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Address
                </label>
                <textarea
                  value={formData.businessAddress}
                  onChange={(e) =>
                    handleInputChange("businessAddress", e.target.value)
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="Enter your business address"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <button
              onClick={fetchSettings}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />
              <span>Refresh</span>
            </button>

            <button
              onClick={handleSaveAll}
              disabled={saving}
              className="flex items-center space-x-2 bg-[#305669] text-white px-6 py-2 rounded-lg hover:bg-[#1785A] disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{saving ? "Saving..." : "Save All Settings"}</span>
            </button>
          </div>

          {/* Current Settings Display */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Current Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(settings).map(([key, value]) => (
                <div key={key} className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 capitalize">
                    {key.replace(/([A-Z])/g, " $1").trim()}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {typeof value === "number" &&
                    (key.includes("Charge") || key.includes("Threshold"))
                      ? `₹${value}`
                      : typeof value === "number" && key.includes("Limit")
                      ? `${value} km`
                      : value?.toString() || "Not set"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;
